// The one gutter between the sidebar and a page's content.
//
// AppShell's content area and LiveBusinessShell's <Outlet/> add no padding, so
// every page used to choose its own: 32 on most business pages, 24 on firm and
// personal pages, and none at all on a few (banking rules, maintenance) — which
// put their headings flush against the sidebar. A page's outermost wrapper is
// a PageFrame (or, for a styled-component page, uses pageGutterCss) so the
// gutter is decided here and nowhere else.
//
// 32 on desktop matches gridSystemTokens.container.padding.desktop and the
// business ShellHeader above the page; 16 on a phone matches what ShellHeader
// drops to at the same breakpoint.

import React from 'react';
import { YStack } from 'tamagui';
import type { YStackProps } from 'tamagui';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export const PAGE_GUTTER = { desktop: 32, phone: 16 } as const;

/** The phone cut-off ShellHeader and the field-service frames already use. */
export const PHONE_MEDIA_QUERY = '(max-width: 767px)';

export const usePageGutter = (): number =>
  useMediaQuery(PHONE_MEDIA_QUERY) ? PAGE_GUTTER.phone : PAGE_GUTTER.desktop;

/** For styled-components pages that cannot render a PageFrame. */
export const pageGutterCss = `
  padding: ${PAGE_GUTTER.desktop}px;

  @media ${PHONE_MEDIA_QUERY} {
    padding: ${PAGE_GUTTER.phone}px;
  }
`;

/**
 * Left and right only, for a band whose vertical rhythm is its own — a hero,
 * a sub-nav, the content under a hero. Interpolate it after the band's own
 * padding so it wins the sides.
 */
export const pageGutterInlineCss = `
  padding-left: ${PAGE_GUTTER.desktop}px;
  padding-right: ${PAGE_GUTTER.desktop}px;

  @media ${PHONE_MEDIA_QUERY} {
    padding-left: ${PAGE_GUTTER.phone}px;
    padding-right: ${PAGE_GUTTER.phone}px;
  }
`;

// Padding is omitted from the props on purpose: a page that overrides it is
// the inconsistency this exists to remove. Pass paddingTop={0} etc. only where
// a full-bleed child (a hero, a sticky bar) needs the edge.
export type PageFrameProps = Omit<YStackProps, 'padding'>;

export const PageFrame = React.forwardRef<React.ElementRef<typeof YStack>, PageFrameProps>(
  (props, ref) => {
    const gutter = usePageGutter();
    return <YStack ref={ref} padding={gutter} {...props} />;
  },
);
PageFrame.displayName = 'PageFrame';

export default PageFrame;
